// src/components/CampaignWizard/StepReview.tsx
import React, { useState } from "react";
import {
	Row,
	Col,
	Card,
	CardBody,
	CardHeader,
	Badge,
	ListGroup,
	ListGroupItem,
	TabContent,
	TabPane,
	Nav,
	NavItem,
	NavLink,
	Alert
} from "reactstrap";
import { ChannelType, CHANNEL_LABELS } from "./StepChannels";
import { AudienceData } from "store/campaigns/types";
import { Template } from "store/templates/types";

const recipientOptions = [
	{
		label: "Alice Johnson",
		vars: {
			name: "Alice Johnson",
			first_name: "Alice",
			last_name: "Johnson",
			country: "India",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		label: "Bob Smith",
		vars: {
			name: "Bob Smith",
			first_name: "Bob",
			last_name: "Smith",
			country: "Nepal",
			gender: "Male",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		label: "Priya Patel",
		vars: {
			name: "Priya Patel",
			first_name: "Priya",
			last_name: "Patel",
			country: "UK",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	}
];

// Utility function from earlier
function replaceVars(template: string, vars: Record<string, string | undefined>) {
	if (!template) return "";
	return template.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
		const v = vars[key.trim()];
		return v === undefined ? `{{${key}}}` : v;
	});
}

export interface StepReviewProps {
	basicInfo: { name: string; description: string; campaign_type: string };
	channels: any[];
	audience: AudienceData;
	schedule: { immediate: boolean; scheduledTime: Date | null; localTime: boolean; recurrence?: any };
	fallbackRules: Array<{ fromChannel: string; onEvents: string[]; toChannel: string; delayHours: number }>;
	quota?: {
		quotaEnabled?: boolean;
		label?: string;
		managedExternally: boolean;
		creditsTotal?: number;
		creditsUsed?: number;
		creditsRemaining?: number;
		resetAt?: string;
		costEstimate?: {
			amount: string;
			currency?: string;
		};
	};
}

const StepReview: React.FC<StepReviewProps> = ({
	basicInfo,
	channels,
	audience,
	schedule,
	fallbackRules,
	quota
}) => {
	const enabled = channels.map((c) => c.channel);
	const scheduleLabel = schedule.immediate
		? "Immediately"
		: schedule.scheduledTime
			? `${schedule.scheduledTime.toLocaleString()}${schedule.localTime ? " (Local)" : ""}`
			: "—";
	const [previewTab, setPreviewTab] = React.useState<string>(enabled[0] || "EMAIL");
	const [recipient, setRecipient] = useState(recipientOptions[0]);

	// Use prop if passed, else state
	const vars = recipient.vars;

	function applyWhatsappMapping(templateText: any, mappingArr: any, recipientVars: { [x: string]: any; name?: string; first_name?: string; last_name?: string; country?: string; gender?: string; AGENCY_NAME?: string; }) {
		let result = templateText;
		(mappingArr || []).forEach((m: { placeholder: string; mappedTo: { fieldKey: any; }; }, idx: number) => {
		  const token = m.placeholder || `{{${idx + 1}}}`;
		  const field = m.mappedTo?.fieldKey;
		  const value = recipientVars[field] ?? token;
		  result = result.split(token).join(value);
		});
		return result;
	  }

	// Generate preview per channel/tab
	const getPreviewContent = (ch: string) => {
		const channelObj = channels.find(c => c.channel === ch);
		if (!channelObj || !channelObj.template_content) return null;
	  
		if (ch === "WHATSAPP" && channelObj.template_content) {
		  const { data, mapping } = channelObj.template_content;
		  return (
			<pre className="border rounded p-3" style={{ minHeight: 120, whiteSpace: "pre-wrap" }}>
			  {applyWhatsappMapping(data, mapping, vars)}
			</pre>
		  );
		}
		if (ch === "EMAIL" && channelObj.template_content) {
		  const content = channelObj.template_content;
		  return (
			<div
			  className="border rounded p-3"
			  style={{ minHeight: 120, overflowY: "auto", maxHeight: 300 }}
			  dangerouslySetInnerHTML={{
				__html: replaceVars(content.template || content.body || content.html || "", vars),
			  }}
			/>
		  );
		}
		return <div className="text-muted">No template selected or no content available.</div>;
	  };
	  

	// Audience count logic (static or dynamic)
	const audienceCount =
		audience.type === "STATIC"
			? audience.imported_count
			: audience.estimated_count;

	// QUOTA / COST / UNLIMITED Logic
	console.log("Quota data:", quota);
	let infoContent = null;
	if (quota?.quotaEnabled) {
		const left = quota.creditsRemaining ?? 0;
		const willSendCount = Math.min(audienceCount, left);
		infoContent =
			left === 0 ? (
				<Alert color="danger" className="mb-3">
					<i className="ri-error-warning-line me-1" />
					You have <b>0</b> credits left for this integration. No messages will be sent.
				</Alert>
			) : willSendCount < audienceCount ? (
				<Alert color="warning" className="mb-3">
					<i className="ri-error-warning-line me-1" />
					Only <b>{willSendCount}</b> out of {audienceCount} recipients will be reached due to quota ({left} left).
				</Alert>
			) : (
				<Alert color="info" className="mb-3">
					<i className="ri-speed-up-line me-1" />
					Quota available: <b>{left}</b> / {quota.creditsTotal} credits left.
					<br />
					{quota.resetAt && (
						<span className="small text-muted">
							Quota resets on {new Date(quota.resetAt).toLocaleDateString()}.
						</span>
					)}
				</Alert>
			);
	} else if (quota?.costEstimate) {
		infoContent = (
			<Alert color="info" className="mb-3">
				<i className="ri-price-tag-3-line me-1" />
				Estimated cost for this campaign:{" "}
				<b>
					{quota.costEstimate.amount} {quota.costEstimate.currency || ""}
				</b>
				<br />
				<span className="small text-muted">
					Please ensure sufficient balance to cover this cost.
				</span>
			</Alert>
		);
	} else if (quota && !quota.quotaEnabled && !quota.costEstimate) {
		infoContent = (
		  <Alert color="success" className="mb-3">
			<i className="ri-checkbox-circle-line me-1" />
			{quota.managedExternally
			  ? (quota.label || <>This integration’s quota is <b>managed by the external service</b>. Limits may apply as per your provider.</>)
			  : <>This integration has <b>unlimited</b> usage for now.</>
			}
		  </Alert>
		);
	  }


	return (
		<>
			<h4 className="mb-5 text-primary fw-bold">Review Your Campaign</h4>
			{infoContent}
			<Row className="gx-4 gy-4">
				{/* Details */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Details</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<ListGroup flush>
								<ListGroupItem className="px-0 py-1">
									<strong>Name:</strong> {basicInfo.name}
								</ListGroupItem>
								<ListGroupItem className="px-0 py-1">
									<strong>Type:</strong> {basicInfo.campaign_type}
								</ListGroupItem>
								{basicInfo.description && (
									<ListGroupItem className="px-0 py-1 text-muted">
										{basicInfo.description}
									</ListGroupItem>
								)}
							</ListGroup>
						</CardBody>
					</Card>
				</Col>

				{/* Channels */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Channels</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{channels.length > 0 ? (
								channels.map((c: any) => (
									<div key={c.channel} className="mb-3 d-flex align-items-center">
										{c.install_summary?.logo && (
											<img
												src={c.install_summary.logo}
												alt={c.install_summary.app_name}
												width={20}
												height={20}
												className="me-2"
												style={{ borderRadius: 4 }}
											/>
										)}
										<span className="fw-semibold">{c.install_summary?.app_name || c.channel}</span>
										<span className="text-muted small ms-2">{c.install_summary?.account_name}</span>
										{c.template_summary?.name && (
											<Badge color="info" className="ms-2">
												{c.template_summary.name}
											</Badge>
										)}
									</div>
								))
							) : (
								<span className="text-muted">None selected</span>
							)}
						</CardBody>
					</Card>
				</Col>


				{/* Audience */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Audience</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<div>
								<strong>
									{audience.type === "STATIC" ? "Static CSV" : "Dynamic Filter"}
								</strong>
							</div>
							<div className="mt-2 text-muted">{audience.type === "STATIC"
								? `CSV (${audience.imported_count ?? 0} rows`
								: `Filter (${audience?.filter_groups?.length} groups`}</div>
						</CardBody>
					</Card>
				</Col>

				{/* Schedule */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Schedule</strong>
						</CardHeader>
						<CardBody className="pt-3">{scheduleLabel}</CardBody>
					</Card>
				</Col>

				{/* Fallback Rules */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Fallback Rules</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{fallbackRules.length > 0 ? (
								fallbackRules.map((r, i) => (
									<div key={i} className="mb-2">
										<strong>
											{CHANNEL_LABELS[r.fromChannel as ChannelType]} →{" "}
											{CHANNEL_LABELS[r.toChannel as ChannelType]}
										</strong>
										<div className="small text-muted">
											On {r.onEvents.join(", ") || "any"} after {r.delayHours}h
										</div>
									</div>
								))
							) : (
								<span className="text-muted">None defined</span>
							)}
						</CardBody>
					</Card>
				</Col>
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>
								{quota?.quotaEnabled
									? "Quota"
									: quota?.costEstimate
										? "Estimated Cost"
										: "Usage"}
							</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{quota?.quotaEnabled ? (() => {
								// fallback to 0 if undefined
								const creditsRemaining = quota.creditsRemaining ?? 0;
								const creditsTotal = quota.creditsTotal ?? 0;
								return (
									<>
										<div>
											<strong>Remaining Credits:</strong> {creditsRemaining} / {creditsTotal}
										</div>
										<div className="text-muted small">
											{quota.resetAt && `Quota resets on ${new Date(quota.resetAt).toLocaleDateString()}`}
										</div>
										<div>
											<strong>
												{creditsRemaining === 0
													? "No messages will be sent. Please top-up credits."
													: creditsRemaining < audienceCount
														? `Only ${creditsRemaining} recipients will be reached due to quota limit.`
														: "All recipients will be reached."
												}
											</strong>
										</div>
									</>
								);
							})() : quota?.costEstimate ? (
								<>
									<div>
										<strong>Estimated Cost:</strong> {quota.costEstimate.amount} {quota.costEstimate.currency || ""}
									</div>
									<div className="text-muted small">
										Please ensure sufficient balance to cover this cost.
									</div>
								</>
							) : (
								<div>
								  <strong>
									{quota?.managedExternally
									  ? "Managed by External Service"
									  : "Unlimited"}
								  </strong>
								  <br />
								  {quota?.managedExternally
									? "Quota is controlled by your provider (e.g., Gupshup, Meta, etc)."
									: "No quota or cost restriction for this campaign."
								  }
								</div>
							  )}
						</CardBody>
					</Card>
				</Col>



				{/* Live Preview */}
				{enabled.length > 0 && (
					<Col md={12}>
						<Card className="border-0 shadow-sm">
							<CardHeader className="bg-light border-0 d-flex align-items-center">
								<strong className="me-3">Preview</strong>
								{/* Recipient select for personalization */}
								<div className="me-3" style={{ minWidth: 230 }}>
									<select
										className="form-select"
										value={recipient.label}
										onChange={e => setRecipient(recipientOptions.find(r => r.label === e.target.value)!)}
									>
										{recipientOptions.map(opt =>
											<option key={opt.label} value={opt.label}>{opt.label}</option>
										)}
									</select>
								</div>
								<Nav pills>
									{enabled.map((ch) => (
										<NavItem key={ch}>
											<NavLink
												className={previewTab === ch ? "active" : ""}
												onClick={() => setPreviewTab(ch)}
											>
												{channels.find((c) => c.channel === ch)?.install_summary?.logo && (
													<img
														src={channels.find((c) => c.channel === ch)?.install_summary.logo}
														alt=""
														width={16}
														height={16}
														className="me-1"
													/>
												)}
												{CHANNEL_LABELS[ch as ChannelType]}
											</NavLink>
										</NavItem>
									))}
								</Nav>
							</CardHeader>
							<CardBody className="pt-3">
								<TabContent activeTab={previewTab}>
									{enabled.map(ch => (
										<TabPane tabId={ch} key={ch}>
											{getPreviewContent(ch)}
										</TabPane>
									))}
								</TabContent>
							</CardBody>
						</Card>
					</Col>
				)}

			</Row>
		</>
	);
};

export default StepReview;
